import { html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

import { LoanLightElement } from "@/legacy-components/loanlight-element";
import { getSvg, SvgKey } from "@/utils/getSvgs";
import { handleUnsafeHtml } from "@/utils/handleUnsafeHtml";
import {
  dispatchLoanLightEvent,
  withEventErrorHandling,
} from "@/utils/handleEvents";
import { ButtonClickEventDetail } from "@/types/events";
import { LOANLIGHT_EVENTS } from "@/constants/events";

@customElement("loanlight-button")
export class LoanLightButton extends LoanLightElement {
  @property({ type: String }) declare text: string;
  @property({ type: String }) declare icon?: SvgKey;
  @property({ type: String })
  declare iconPosition: "before" | "after";
  @property({ type: Function }) declare onClick?: () => void;
  @property({ type: String })
  declare variant: "primary" | "secondary" | "outline" | "error";
  @property({ type: String })
  declare size: "default" | "large" | "small" | "mini";
  @property({ type: Boolean }) declare disabled: boolean;
  @property({ type: Boolean, reflect: true }) declare fullWidth: boolean;
  @property({ type: String, attribute: "aria-label" })
  declare ariaLabel: string;
  @property({ type: String }) declare title: string;

  constructor() {
    super();
    this.text = "";
    this.icon = undefined;
    this.iconPosition = "before";
    this.onClick = undefined;
    this.variant = "primary";
    this.size = "default";
    this.disabled = false;
    this.fullWidth = false;
    this.ariaLabel = "";
    this.title = "";
  }

  // TODO: add remaining variants
  static styles = css`
    :host {
      display: inline-flex;
      --primary-color: #294b5f;
      --primary-hover: #163143;
      --primary-disabled: #687d87;
      --primary-focus: #294b5f;
      --secondary-color: #f8fbfc;
      --secondary-hover: #f1f6f9;
      --secondary-disabled: #f3f6f7;
      --secondary-focus: #f1f6f9;
      --outline-color: #e5eaed;
      --outline-hover: #f8fbfc;
      --outline-disabled: #f3f6f7;
      --outline-focus: #f8fbfc;
      --error-color: #b33c3c;
      --error-hover: #993030;
      --error-disabled: #b33c3c;
      --error-focus: #b33c3c;
    }

    :host([fullwidth]) {
      width: 100%;
    }

    button {
      justify-content: center;
      align-items: center;
      gap: 8px;
      border-radius: 8px;
      cursor: pointer;
      transition: background-color 0.2s ease-in-out;
      border: none;
      font-family: inherit;
      display: flex;
    }

    button:disabled {
      cursor: default;
    }

    button:focus {
      box-shadow: 0 0 0 3px #3182ce;
    }

    .button--default {
      min-height: 36px;
      padding: 7.5px 16px;
      font-size: 14px;
    }

    .button--large {
      min-height: 40px;
      padding: 9.5px 24px;
      font-size: 14px;
    }

    .button--small {
      min-height: 32px;
      padding: 5.5px 12px;
      font-size: 14px;
    }

    .button--mini {
      min-height: 24px;
      padding: 3px 8px;
      font-size: 12px;
    }

    .button--full-width {
      width: 100%;
    }

    .button__icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .button__icon svg {
      width: 16px;
      height: 16px;
    }

    .button--primary {
      background-color: var(--primary-color);
      color: #ffffff;
    }

    .button--primary:hover {
      background-color: var(--primary-hover);
    }

    .button--primary:disabled {
      background-color: var(--primary-disabled);
    }

    .button--primary:focus {
      background-color: var(--primary-focus);
    }

    .button--secondary {
      background-color: var(--secondary-color);
      color: #163143;
    }

    .button--secondary:hover {
      background-color: var(--secondary-hover);
    }

    .button--secondary:disabled {
      background-color: var(--secondary-disabled);
    }

    .button--secondary:focus {
      background-color: var(--secondary-focus);
    }

    .button--outline {
      background-color: var(--outline-color);
      color: #163143;
    }

    .button--outline:hover {
      background-color: var(--outline-hover);
    }

    .button--outline:disabled {
      background-color: var(--outline-disabled);
    }

    .button--outline:focus {
      background-color: var(--outline-focus);
    }

    .button--error {
      background-color: var(--error-color);
      color: #ffffff;
    }

    .button--error:hover {
      background-color: var(--error-hover);
    }

    .button--error:disabled {
      background-color: var(--error-disabled);
    }

    .button--error:focus {
      background-color: var(--error-focus);
    }
  `;

  private handleClick = withEventErrorHandling(() => {
    if (this.disabled) {
      return;
    }

    const eventDetail: ButtonClickEventDetail = {
      text: this.text,
      variant: this.variant,
      size: this.size,
      disabled: this.disabled,
      icon: this.icon,
      iconPosition: this.iconPosition,
      fullWidth: this.fullWidth,
    };

    dispatchLoanLightEvent(this, LOANLIGHT_EVENTS.BUTTON_CLICK, eventDetail);

    this.onClick?.();
  });

  private renderButtonContent() {
    if (!this.icon) {
      return html`<slot>${this.text}</slot>`;
    }

    const iconElement = html`<span class="button__icon"
      >${handleUnsafeHtml(getSvg(this.icon))}</span
    >`;

    return this.iconPosition === "before"
      ? html`${iconElement}<slot>${this.text}</slot>`
      : html`<slot>${this.text}</slot>${iconElement}`;
  }

  render() {
    return html`
      <button
        type="button"
        class="button--${this.variant} button--${this.size}${this.fullWidth
          ? " button--full-width"
          : ""}"
        @click=${this.handleClick}
        ?disabled=${this.disabled}
        aria-label=${this.ariaLabel || this.text || undefined}
        title=${this.title || undefined}
      >
        ${this.renderButtonContent()}
      </button>
    `;
  }
}
